import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import type { IGroupData, IAccountDataResponse } from '@tool-no-hu/shared/types';
import { AccountSide } from '@tool-no-hu/shared/enums';

import { groupEligibility, resolveGroupMembers, isGroupRunning } from '../utils/group-eligibility';

interface GroupSummaryCardProps {
  group: IGroupData;
  accounts: IAccountDataResponse[];
  selectable: boolean;
  selected: boolean;
  onToggleSelect: (groupId: string) => void;
}

/** 1000 → `1K`, 1.5tr → `1.5M` — cấu hình cược chỉ cần con số gọn. */
function shortAmount(value: number): string {
  if (value >= 1_000_000) return `${String(value / 1_000_000).replace(/\.0$/, '')}M`;
  if (value >= 1_000) return `${String(value / 1_000).replace(/\.0$/, '')}K`;
  return String(value);
}

function gameName(typegame: string): string {
  if (typegame === 'TX') return 'Tài Xỉu';
  if (typegame === 'XD') return 'Xóc Đĩa';
  if (typegame === 'TL') return 'Tứ Linh';
  return typegame;
}

/** Badge màu game — trùng convention `GroupCard.GAME_BADGE_COLORS`. */
const GAME_BADGE_COLORS: Record<string, string> = {
  TX: 'bg-[#3b82f6]/20 text-[#3b82f6]',
  XD: 'bg-[#8b5cf6]/20 text-[#8b5cf6]',
  TL: 'bg-[#f59e0b]/20 text-[#f59e0b]',
};

const MEMBER_COLORS = ['#60a5fa', '#34d399', '#a78bfa', '#fb923c', '#f472b6', '#2dd4bf'];

function sideAmount(group: IGroupData, side: AccountSide): number {
  return group.sideConfigs?.find((s) => s.side === side)?.amount || 0;
}

/** Số dư của 1 member (có thể chưa đồng bộ → '?'). */
function formatBalance(balance: number | '?' | undefined): string {
  if (balance === '?' || balance == null) return '?';
  return balance.toLocaleString('vi-VN');
}

/**
 * Thẻ TÓM TẮT một nhóm — chế độ xem gọn để chọn nhiều nhóm cùng lúc.
 *
 * Giữ đúng những gì cần để chọn: tên + game, thành viên kèm số dư, cấu hình cược
 * side (cấp nhóm), trạng thái, và gợi ý Start/Stop được hay bị bỏ qua (kèm lý
 * do). Điều kiện dùng CHUNG luật với thẻ chi tiết + bulk qua `groupEligibility`.
 */
export function GroupSummaryCard({
  group,
  accounts,
  selectable,
  selected,
  onToggleSelect,
}: GroupSummaryCardProps) {
  const groupId = String(group._id);
  const members = resolveGroupMembers(group, accounts);

  const running = isGroupRunning(members);
  const allLoggedIn = members.length > 0 && members.every((m) => !!m.accessToken);

  const startVerdict = groupEligibility(group, accounts).start;
  const hint: { tone: 'start' | 'stop' | 'skip'; text: string } = running
    ? { tone: 'stop', text: 'Stop được' }
    : startVerdict.ok
      ? { tone: 'start', text: 'Start được' }
      : { tone: 'skip', text: `Bỏ qua · ${startVerdict.reason}` };

  const statusPill = running
    ? { cls: 'bg-[#22c55e]/10 text-[#4ade80]', dot: 'bg-[#22c55e]', text: 'Đang chạy' }
    : allLoggedIn
      ? { cls: 'bg-[#1e2235] text-[#64748b]', dot: 'bg-[#64748b]', text: 'Đã đăng nhập' }
      : { cls: 'bg-[#1e2235] text-[#94a3b8]', dot: 'bg-[#475569]', text: 'Chưa đăng nhập' };

  const chan = sideAmount(group, AccountSide.CHAN);
  const le = sideAmount(group, AccountSide.LE);
  const gameType = group.gameType;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`relative rounded-xl border bg-[#161920] p-3.5 shadow-sm transition-shadow ${
        selected ? 'border-[#3b82f6] ring-2 ring-[#3b82f6]' : 'border-[#252a38]'
      }`}
    >
      {selectable && (
        <button
          type="button"
          onClick={() => onToggleSelect(groupId)}
          aria-pressed={selected}
          aria-label={`${selected ? 'Bỏ chọn' : 'Chọn'} nhóm ${group.name}`}
          className="absolute inset-0 z-20 cursor-pointer rounded-xl"
        />
      )}
      {selectable && (
        <span
          className={`pointer-events-none absolute right-2.5 top-2.5 z-30 grid h-5 w-5 place-items-center rounded-md border-2 transition-colors ${
            selected ? 'border-[#3b82f6] bg-[#3b82f6] text-white' : 'border-[#252a38] bg-[#0d0f14]'
          }`}
        >
          {selected && <Check className="h-3 w-3" />}
        </span>
      )}

      {/* Tên + game */}
      <div className="mb-2.5 flex items-center gap-2 pr-6">
        <h3 className="truncate text-sm font-bold text-[#e2e8f0]">{group.name}</h3>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
            GAME_BADGE_COLORS[gameType] ?? 'bg-[#1e2235] text-[#64748b]'
          }`}
          title={gameName(gameType)}
        >
          {gameType}
        </span>
      </div>

      {/* Thành viên + số dư */}
      <div className="mb-2.5 space-y-1">
        {members.length > 0 ? (
          members.map((member, index) => (
            <div key={member.username} className="flex items-center gap-2 text-[11.5px]">
              <span
                className="min-w-0 flex-1 truncate font-medium"
                style={{ color: MEMBER_COLORS[index % MEMBER_COLORS.length] }}
              >
                {member.username}
              </span>
              <span className="shrink-0 font-mono text-[11px] tabular-nums text-[#e2e8f0]">
                {formatBalance(member.balance)}
              </span>
            </div>
          ))
        ) : (
          <span className="text-[11.5px] text-[#94a3b8]">Chưa có thành viên</span>
        )}
      </div>

      {/* Cấu hình cược side (cấp nhóm) */}
      <div className="mb-2.5 flex items-center gap-2 font-mono text-[11px] text-[#64748b]">
        <span>CHẴN:{shortAmount(chan)}</span>
        <span className="text-[#475569]">·</span>
        <span>LẺ:{shortAmount(le)}</span>
      </div>

      {/* Trạng thái + gợi ý hành động */}
      <div className="flex flex-wrap items-center gap-2 border-t border-[#252a35] pt-2.5">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusPill.cls}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusPill.dot}`} />
          {statusPill.text}
        </span>
        <span
          className={`ml-auto inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold ${
            hint.tone === 'start'
              ? 'bg-[#22c55e]/10 text-[#4ade80]'
              : hint.tone === 'stop'
                ? 'bg-[#ef4444]/10 text-[#ef4444]'
                : 'bg-[#f59e0b]/10 text-[#f59e0b]'
          }`}
        >
          {hint.text}
        </span>
      </div>
    </motion.div>
  );
}
